import { ChecklistItemRecord } from "models/view-models/block-configurations/checklist-item-record";
import useChecklistItem from "utilities/hooks/domain/notebooks/pages/checklists/use-checklist-item";
import React, { useState } from "react";
import useChecklistItemKeyPresses from "utilities/hooks/domain/notebooks/pages/checklists/use-checklist-item-key-presses";
import ChecklistItemDisplay from "./checklist-item-display";
import { ChecklistStatus } from "models/enumerations/notebooks/checklist-item-status";
import { MergeChecklistItemEvent } from "utilities/hooks/domain/notebooks/pages/checklists/use-split-or-merge-checklist-items";
import { ChecklistItemNoteRecord } from "models/view-models/checklist-item-notes/checklist-item-note-record";
import { useRunOnUnmount } from "utilities/hooks/use-run-on-unmount";
import useAutoSave from "utilities/hooks/use-auto-save";
import UsePageLimits from "pages/notebooks/use-page-limits";

interface ChecklistItemEditorProps {
    index: number;
    isNumbered: boolean;
    item: ChecklistItemRecord;
    onItemModified: (updatedItem: ChecklistItemRecord) => void;
    onItemSaved: (updatedItem: ChecklistItemRecord) => void;
    onItemSaving: () => void;
    onMergeChecklistItem: (
        event: MergeChecklistItemEvent,
        index: number
    ) => void;
    onSplitChecklistItem: (index: number, cursorPosition: number) => void;
    showStatus: boolean;
    totalItemCount: number;
    saveInProgress: boolean;
}

const CSS_CLASS_NAME = "c-checklist-item-editor";

const ChecklistItemEditor = ({
    index,
    isNumbered,
    item,
    onItemModified,
    onItemSaved,
    onItemSaving,
    onMergeChecklistItem,
    onSplitChecklistItem,
    showStatus,
    totalItemCount,
    saveInProgress,
}: ChecklistItemEditorProps) => {
    const [pendingChange, setPendingChange] = useState<(() => void) | null>(
        null
    );
    const { updateChecklistItem } = useChecklistItem();
    const { pageLimits } = UsePageLimits();
    const onSplitItem = (index: number, cursorPosition: number) => {
        setPendingChange(() => null);
        onSplitChecklistItem(index, cursorPosition);
    };
    const onItemTextChanged = async (newText: string) => {
        const isPastMax =
            pageLimits?.checklistBlockCharacterLimit &&
            newText.length > pageLimits.checklistBlockCharacterLimit;
        if (item.text.length < newText.length && isPastMax) {
            return;
        }
        const updatedItem = item.with({ text: newText });
        onItemModified(updatedItem);
        setPendingChange(
            () => async () => await handleUpdateChecklistItemText(newText)
        );
    };

    const onMergeWithPreviousItem = (index: number) => {
        setPendingChange(() => null);
        onMergeChecklistItem(MergeChecklistItemEvent.MergeWithPrevious, index);
    };

    const onMergeWithNextItem = (index: number) => {
        setPendingChange(() => null);
        onMergeChecklistItem(MergeChecklistItemEvent.MergeWithNext, index);
    };

    const savePendingChanges = async (): Promise<void> => {
        if (pendingChange == null) {
            return;
        }

        pendingChange();
    };

    const { handleKeyDown } = useChecklistItemKeyPresses(
        item,
        onSplitItem,
        onMergeWithPreviousItem,
        onMergeWithNextItem,
        onItemTextChanged
    );

    const handleChecklistItemNoteUpdate = (
        updatedNote?: ChecklistItemNoteRecord
    ): void => {
        onItemSaved(item.with({ note: updatedNote }));
    };

    const handleUpdateChecklistItem = async (
        item: ChecklistItemRecord
    ): Promise<void> => {
        try {
            onItemSaving();

            const updatedItem = await updateChecklistItem(item);

            if (updatedItem == null) {
                throw new Error("Error updating checklist item");
            }

            onItemSaved(updatedItem.with({ note: item.note }));

            setPendingChange(null);
        } catch (error) {
            console.error("Error updating checklist item", error);

            onItemSaved(item);
        }
    };

    const handleUpdateChecklistItemText = async (
        newText: string
    ): Promise<void> => {
        const updatedItem = item.with({ text: newText });

        await handleUpdateChecklistItem(updatedItem);
    };

    const handleUpdateChecklistItemChecked = async (): Promise<void> => {
        const updatedItem = item.with({ isChecked: !item.isChecked });

        handleUpdateChecklistItem(updatedItem);
    };

    const handleUpdateChecklistItemStatus = (newStatus: ChecklistStatus) => {
        const updatedItem = item.with({ status: newStatus });

        handleUpdateChecklistItem(updatedItem);
    };

    useAutoSave({
        delayInSeconds: 2,
        onSave: savePendingChanges,
        value: item.text,
    });

    useRunOnUnmount(() => {
        if (pendingChange == null) return null;
        savePendingChanges();
    });

    return (
        <div className={CSS_CLASS_NAME}>
            <ChecklistItemDisplay
                index={index}
                isEditing={true}
                isNumbered={isNumbered}
                item={item}
                key={item.id}
                onKeyDown={handleKeyDown}
                onChecklistItemNoteUpdate={handleChecklistItemNoteUpdate}
                onTextChange={onItemTextChanged}
                onUpdateChecklistItemChecked={handleUpdateChecklistItemChecked}
                onUpdateChecklistItemStatus={handleUpdateChecklistItemStatus}
                onUpdateChecklistItemText={handleUpdateChecklistItemText}
                printable={false}
                showStatus={showStatus}
                totalItems={totalItemCount}
                saveInProgress={saveInProgress}
            />
        </div>
    );
};

export default ChecklistItemEditor;
